import React from 'react';
import SubscriptionTemplate from './SubscriptionTemplate';
import { displayMessage } from './wsHelpers';
import { connect } from 'react-redux';
import {
	updateAgendaItem,
	updateAttendeeAgendaItem,
	duplicateAttendeeAgendaItem,
	addGroupsToAgendaItem,
	removeGroupsFromAgendaItem,
	addMaterialToAgendaItem,
	deleteMaterialFromAgendaItem,
	updateMaterial,
	addGroupsToMaterial,
	removeGroupsFromMaterial,
} from '@/redux/actions';
import { useParams } from 'react-router-dom';

const WS_AGENDA_ITEM_BY_HTTP = {
	ADD_GROUPS: 'agenda/:agendaItemID/addGroups/PATCH',
	REMOVE_GROUPS: 'agenda/:agendaItemID/removeGroups/PATCH',
	ADD_MATERIAL: 'agenda/:agendaItemID/addMaterial/PATCH',
	DELETE_MATERIAL: 'agenda/:agendaItemID/deleteMaterial/DELETE',
	ADD_GROUPS_TO_MATERIAL: 'agenda/:agendaItemID/addMaterialGroups/PATCH',
	REMOVE_GROUPS_FROM_MATERIAL: 'agenda/:agendaItemID/removeMaterialGroups/PATCH',
	UPDATE_MATERIAL: 'agenda/:agendaItemID/updateMaterial/PATCH',
};

const AgendaItemSubscriptions = props => {
	const {
		socket,
		children,
		addGroupsToAgendaItem,
		removeGroupsFromAgendaItem,
		addMaterialToAgendaItem,
		deleteMaterialFromAgendaItem,
		updateMaterial,
		addGroupsToMaterial,
		removeGroupsFromMaterial,
	} = props;
	const propsToSend = { ...props };
	delete propsToSend.children;
	const { id: agendaItemID } = useParams();

	const subscribeToAgenda = () => {
		socket.emit('agenda/:agendaItemID/SUBSCRIBE', { agendaItemID });
		console.log('⚡️ AgendaItemSubscriptions Mounted ⚡️');
	};

	const unsubscribeToAgenda = () => {
		socket.emit('agenda/:agendaItemID/UNSUBSCRIBE', { agendaItemID });
		console.log(`🚶‍♂️ AgendaItemSubscriptions Unmounted 🚶‍♂️`);
	};

	const listenForAddedGroups = () =>
		socket.on(WS_AGENDA_ITEM_BY_HTTP.ADD_GROUPS, payload => {
			const { data, error } = payload;
			if (error) displayMessage('Error adding groups to agenda item', error);
			else {
				const { agendaItemID } = payload;
				addGroupsToAgendaItem(data, agendaItemID);
				displayMessage('Groups added to agenda item');
			}
		});

	const listenForRemovedGroups = () =>
		socket.on(WS_AGENDA_ITEM_BY_HTTP.REMOVE_GROUPS, payload => {
			const { data, error } = payload;
			if (error) displayMessage('Error removing groups from agenda item', error);
			else {
				const { agendaItemID } = payload;
				removeGroupsFromAgendaItem(data, agendaItemID);
				displayMessage('Groups removed from agenda item');
			}
		});

	const listenForAddedMaterial = () =>
		socket.on(WS_AGENDA_ITEM_BY_HTTP.ADD_MATERIAL, payload => {
			const { data, error } = payload;
			if (error) displayMessage('Material could not be added to agenda item', error);
			else {
				const { socketID } = payload;
				if (socketID !== socket.id) addMaterialToAgendaItem(data);
				//displayMessage('Material added to agenda item');
			}
		});

	const listenForDeletedMaterial = () =>
		socket.on(WS_AGENDA_ITEM_BY_HTTP.DELETE_MATERIAL, payload => {
			const { data, error } = payload;
			if (error) displayMessage('Material could not be deleted from agenda item', error);
			else {
				//const { socketID } = payload;
				deleteMaterialFromAgendaItem(data);
				//if(socketID != socket.id) deleteMaterialFromAgendaItem(data);
				displayMessage('Material deleted from agenda item');
			}
		});

	const listenForUpdatedMaterial = () =>
		socket.on(WS_AGENDA_ITEM_BY_HTTP.UPDATE_MATERIAL, payload => {
			const { data, error } = payload;
			if (error) displayMessage('Material could not be updated', error);
			else {
				const { name } = data;
				updateMaterial(data);
				displayMessage(`${name} material updated`);
			}
		});

	const listenForMaterialAddedGroups = () =>
		socket.on(WS_AGENDA_ITEM_BY_HTTP.ADD_GROUPS_TO_MATERIAL, payload => {
			const { data, error } = payload;
			if (error) displayMessage('Error adding groups to agenda item material', error);
			else {
				const { addManyGroupsToMaterial, materialID } = data;
				addGroupsToMaterial(addManyGroupsToMaterial, materialID);
			}
		});

	const listenForMaterialRemovedGroups = () =>
		socket.on(WS_AGENDA_ITEM_BY_HTTP.REMOVE_GROUPS_FROM_MATERIAL, payload => {
			const { data, error } = payload;
			if (error) displayMessage('Error removing groups from agenda item material', error);
			else {
				const { removeManyGroupsFromMaterial, materialID } = data;
				removeGroupsFromMaterial(removeManyGroupsFromMaterial, materialID);
			}
		});

	const listeners = () => [
		listenForAddedGroups,
		listenForRemovedGroups,
		listenForAddedMaterial,
		listenForDeletedMaterial,
		listenForUpdatedMaterial,
		listenForMaterialAddedGroups,
		listenForMaterialRemovedGroups,
	];

	return (
		<>
			<SubscriptionTemplate
				{...props}
				sub={subscribeToAgenda}
				unsub={unsubscribeToAgenda}
				listeners={listeners()}
				routes={Object.values(WS_AGENDA_ITEM_BY_HTTP)}
			/>
			{React.cloneElement(children, propsToSend)}
		</>
	);
};

const msp = state => ({
	socket: state.ws.socket,
	auth0Token: state.auth.auth0Token,
});

const mdp = dispatch => ({
	updateAttendeeAgenda: agendaItem => dispatch(updateAttendeeAgendaItem(agendaItem)),
	updateAgendaItem: agendaItem => dispatch(updateAgendaItem(agendaItem)),
	duplicateAttendeeAgendaItem: agendaItem => dispatch(duplicateAttendeeAgendaItem(agendaItem)),
	addGroupsToAgendaItem: (groupIDs, agendaItemID) =>
		dispatch(addGroupsToAgendaItem(groupIDs, agendaItemID)),
	removeGroupsFromAgendaItem: (groupIDs, agendaItemID) =>
		dispatch(removeGroupsFromAgendaItem(groupIDs, agendaItemID)),
	addMaterialToAgendaItem: materials => dispatch(addMaterialToAgendaItem(materials)),
	deleteMaterialFromAgendaItem: materialID => dispatch(deleteMaterialFromAgendaItem(materialID)),
	updateMaterial: material => dispatch(updateMaterial(material)),
	addGroupsToMaterial: (groupIDs, materialID) =>
		dispatch(addGroupsToMaterial(groupIDs, materialID)),
	removeGroupsFromMaterial: (groupIDs, materialID) =>
		dispatch(removeGroupsFromMaterial(groupIDs, materialID)),
});

export default connect(msp, mdp)(AgendaItemSubscriptions);
